import { algoliasearch } from "algoliasearch";

export const ALGOLIA_POSTS_INDEX_NAME =
  process.env.NEXT_PUBLIC_ALGOLIA_POSTS_INDEX_NAME?.trim() || "jcg_posts";
export const ALGOLIA_PROBLEMS_INDEX_NAME =
  process.env.NEXT_PUBLIC_ALGOLIA_PROBLEMS_INDEX_NAME?.trim() ||
  "jcg_problems";
export const ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME =
  process.env.NEXT_PUBLIC_ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME?.trim() ||
  "jcg_query_suggestions";
// 기존 콘텐츠 검색 코드 호환용 alias
export const ALGOLIA_INDEX_NAME = ALGOLIA_POSTS_INDEX_NAME;

export type AlgoliaSearchKeyTarget = "posts" | "problems";
export type AlgoliaSearchResultTarget = "all" | "posts" | "problems";

export type AlgoliaSearchCredentials = {
  appId: string;
  apiKey: string;
  indexName: string;
  validUntil: number;
  expiresAt: string;
  accessTier?: "free" | "premium";
  secured: true;
};

type SearchClientBundle = {
  client: ReturnType<typeof algoliasearch>;
  credentials: AlgoliaSearchCredentials;
};

const REFRESH_SKEW_SECONDS = 30;
const credentialsCache = new Map<AlgoliaSearchKeyTarget, SearchClientBundle>();

export function getAlgoliaSearchTargetByIndex(
  indexName: string
): AlgoliaSearchKeyTarget | null {
  if (indexName === ALGOLIA_POSTS_INDEX_NAME) return "posts";
  if (indexName === ALGOLIA_PROBLEMS_INDEX_NAME) return "problems";
  return null;
}

export async function getAlgoliaSearchCredentials(
  target: AlgoliaSearchKeyTarget
): Promise<AlgoliaSearchCredentials> {
  const cached = credentialsCache.get(target);
  const nowSeconds = Math.floor(Date.now() / 1000);
  if (
    cached &&
    nowSeconds + REFRESH_SKEW_SECONDS < cached.credentials.validUntil
  ) {
    return cached.credentials;
  }

  const response = await fetch(`/api/algolia/search-key?target=${target}`, {
    cache: "no-store",
  });
  const body = (await response.json().catch(() => null)) as
    | (Partial<AlgoliaSearchCredentials> & { error?: string })
    | null;

  if (!response.ok || !body?.appId || !body.apiKey || !body.indexName) {
    throw new Error(body?.error || "Algolia 검색 키 발급에 실패했습니다.");
  }

  const credentials = body as AlgoliaSearchCredentials;
  credentialsCache.set(target, {
    client: algoliasearch(credentials.appId, credentials.apiKey),
    credentials,
  });
  return credentials;
}

export async function getAlgoliaSearchClient(
  target: AlgoliaSearchKeyTarget
): Promise<SearchClientBundle> {
  const cached = credentialsCache.get(target);
  const nowSeconds = Math.floor(Date.now() / 1000);
  if (
    cached &&
    nowSeconds + REFRESH_SKEW_SECONDS < cached.credentials.validUntil
  ) {
    return cached;
  }

  const credentials = await getAlgoliaSearchCredentials(target);
  const bundle = credentialsCache.get(target);
  if (bundle) return bundle;

  const client = algoliasearch(credentials.appId, credentials.apiKey);
  const nextBundle = { client, credentials };
  credentialsCache.set(target, nextBundle);
  return nextBundle;
}

export type SearchHit = {
  objectID: string;
  recordType?: "post" | "problem";
  __indexName?: string;
  __queryID?: string;
  __position?: number;
  title?: string;
  description?: string;
  content?: string;
  slug?: string;
  url?: string;
  pageUrl?: string;
  anchor?: string;
  problemId?: string;
  urlSource?: "embedded" | "forum";
  category?: string;
  subject?: string;
  scope?: string;
  accessTier?: "free" | "premium";
  examSessions?: string[];
  problemType?: "기초" | "비추천" | "기출변형" | "기출" | string | null;
  isCustom?: boolean;
  isFinal?: boolean;
  isPractical?: boolean;
  isWritten?: boolean;
  tags?: string[];
  _highlightResult?: {
    title?: {
      value: string;
      matchLevel: string;
    };
    description?: {
      value: string;
      matchLevel: string;
    };
    content?: {
      value: string;
      matchLevel: string;
    };
  };
  _snippetResult?: {
    content?: {
      value: string;
      matchLevel: string;
    };
  };
};

export type ErrorNoteSearchHit = {
  objectID: string;
  problemId: string;
  noteId: string;
  title?: string;
  content: string;
  scope?: string;
  examSessionId?: string;
  createdAt?: number;
  updatedAt?: number;
};

export type QuerySuggestionHit = {
  objectID: string;
  query: string;
  popularity?: number;
  nb_words?: number;
};
